import React, { useCallback, useContext, useState, useRef } from 'react';
import type { NodeTone } from './nodeTheme';
import { CanvasNodeDragContext, snapToGrid } from '../canvasNodeDrag';
import { Position } from '../../types';
import { GripHorizontal, MessageSquare, PenLine, SmilePlus, X } from 'lucide-react';
import { REACTIONS } from '../../utils/ideas';
import { nodeReactionsOf, reactorsLabel, type NodeReactions } from '../../utils/nodeReactions';
import { useCloseOnOutside } from '../ideas/parts';

interface BaseNodeProps {
  id: string;
  position: Position;
  size?: { width: number; height: number };
  isSelected?: boolean;
  isViewOnly?: boolean; // When true, disables drag, resize, delete, and connections
  onPositionChange: (id: string, newPos: Position) => void;
  onResize?: (id: string, newSize: { width: number; height: number }) => void;
  onDelete?: (id: string) => void;
  /** additive: Shift, Ctrl or Cmd was held, so the node is added to or taken out of the selection. */
  onSelect: (id: string, additive?: boolean) => void;
  onConnectStart?: (id: string, e: React.PointerEvent) => void;
  onConnectEnd?: (id: string, e: React.PointerEvent) => void;
  children: React.ReactNode;
  title?: string;
  /** The icon of the node's kind, shown in the round chip before the title. */
  titleIcon?: React.ReactNode;
  /** The colour of the node's kind (components/nodes/nodeTheme.tsx): its header, icon chip, ports and selection ring. */
  tone?: NodeTone;
  hideConnectors?: boolean; // For nodes that shouldn't have connections (e.g., Storage Browser)
  /** The shortest the node may be; a folded node is shorter than the usual 150. */
  minHeight?: number;
  /** Reactions on the node, as on Ideas cards: emoji → who gave it (utils/nodeReactions.ts) */
  reactions?: NodeReactions;
  /** Adds or takes back the signed-in person's reaction; without it the reactions are only shown */
  onReact?: (id: string, emoji: string) => void;
  /** The signed-in person, whose reactions show as theirs */
  myId?: string;
  /** The names of the workspace's people by id, for who gave a reaction */
  reactorNames?: Record<string, string>;
  /** Opens this node's comments in the panel at the right (components/NodeCommentsPanel.tsx) */
  onOpenComments?: (id: string) => void;
  /** How many comments the node has: shown on its comment button */
  commentCount?: number;
  /** Its comments are open in the panel: the node is marked, so it is clear which one they are about */
  commentsOpen?: boolean;
  /** The node can be renamed in its header (a double-click on it, or the pencil shown on hover) */
  onRename?: (id: string, title: string) => void;
  /** The name a node goes back to when its name is cleared */
  defaultTitle?: string;
  /** What the card shows in place of its body when the canvas is zoomed out far (CardFarView, utils/farZoom.ts) */
  farView?: React.ReactNode;
}

const isAdditive = (e: React.PointerEvent) => e.shiftKey || e.ctrlKey || e.metaKey;

const BaseNodeComponent: React.FC<BaseNodeProps> = ({
  id,
  position,
  size,
  isSelected,
  isViewOnly = false,
  onPositionChange,
  onResize,
  onDelete,
  onSelect,
  onConnectStart,
  onConnectEnd,
  children,
  title,
  titleIcon,
  tone = 'ink',
  hideConnectors = false,
  minHeight = 150,
  reactions,
  onReact,
  myId,
  reactorNames,
  onOpenComments,
  commentCount = 0,
  commentsOpen = false,
  onRename,
  defaultTitle,
  farView,
}) => {
  const [renaming, setRenaming] = useState(false);
  // The emojis to react with, opened from the header
  const [picking, setPicking] = useState(false);
  const closePicker = useCallback(() => setPicking(false), []);
  const pickerRef = useCloseOnOutside<HTMLSpanElement>(picking, closePicker);
  const shownReactions = nodeReactionsOf(reactions, myId);
  // Those who may only view react too (their reactions live in the cloud, as comments do): the app
  // leaves onReact out when nobody is signed in
  const mayReact = !!onReact;
  // Once a node has comments or reactions (or one of its menus is open), its round buttons stay in
  // view, so the pills beside them never move; a quiet node shows them only when pointed at
  const hasSocial = commentCount > 0 || shownReactions.length > 0 || commentsOpen || picking;
  const react = (emoji: string) => { setPicking(false); onReact?.(id, emoji); };
  const mayRename = !!onRename && !isViewOnly;
  const finishRename = (name: string | null) => {
    setRenaming(false);
    if (name === null || !onRename) return;
    const next = name.replace(/\s+/g, ' ').trim() || defaultTitle || title || '';
    if (next && next !== title) onRename(id, next);
  };
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  const initialNodePos = useRef<{ x: number; y: number } | null>(null);
  const initialSize = useRef<{ width: number; height: number } | null>(null);
  const nodeRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  // On a canvas, a drag moves the node on screen and reports it once, when dropped
  const canvasDrag = useContext(CanvasNodeDragContext);
  // How far the pointer has taken the node, in canvas units; null until it moves
  const dragOffset = useRef<{ x: number; y: number } | null>(null);
  const dragScale = useRef(1);
  const resizeHandleRef = useRef<HTMLDivElement>(null);

  // --- DRAG LOGIC ---
  const handlePointerDown = (e: React.PointerEvent) => {
    // Prevent drag if clicking close or resize
    if ((e.target as HTMLElement).closest('.no-drag')) return;
    if (isAdditive(e)) {
      // Shift, Ctrl or Cmd on the title bar adds the node to the selection or takes it out; no drag
      e.preventDefault();
      e.stopPropagation();
      onSelect(id, true);
      return;
    }
    if (isViewOnly) return; // View-only: no dragging
    
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    onSelect(id);

    dragStartPos.current = { x: e.clientX, y: e.clientY };
    initialNodePos.current = { ...position };
    dragOffset.current = null;
    // The zoom does not change during a drag: read it once, not on every move
    dragScale.current = getParentScale();
    canvasDrag?.begin(id);

    // Use the drag handle ref for pointer capture, not e.target (which might be a child)
    if (dragHandleRef.current) {
      dragHandleRef.current.setPointerCapture(e.pointerId);
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging || !dragStartPos.current || !initialNodePos.current) return;
    e.preventDefault();
    e.stopPropagation();

    const scale = dragScale.current || 1;
    const dx = (e.clientX - dragStartPos.current.x) / scale;
    const dy = (e.clientY - dragStartPos.current.y) / scale;

    if (canvasDrag) {
      // The node follows the pointer; it goes to the grid when it is dropped. Moving it in
      // grid steps looked jerky however fast the frames came
      dragOffset.current = { x: dx, y: dy };
      canvasDrag.move(dx, dy);
      return;
    }
    onPositionChange(id, { x: snapToGrid(initialNodePos.current.x + dx), y: snapToGrid(initialNodePos.current.y + dy) });
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (isDragging) {
      setIsDragging(false);
      if (dragHandleRef.current?.hasPointerCapture(e.pointerId)) {
        dragHandleRef.current.releasePointerCapture(e.pointerId);
      }
      if (canvasDrag) {
        // Dropped: onto the grid, and the new place is reported once (the nodes moving with it
        // follow in the app). A press that never moved leaves the node where it is
        const start = initialNodePos.current;
        const moved = dragOffset.current;
        const dropped = start && moved ? { x: snapToGrid(start.x + moved.x), y: snapToGrid(start.y + moved.y) } : start;
        const offset = start && dropped ? { x: dropped.x - start.x, y: dropped.y - start.y } : { x: 0, y: 0 };
        if (dropped && (offset.x || offset.y)) onPositionChange(id, dropped);
        canvasDrag.end(offset.x, offset.y);
      }
    }
  };

  // --- RESIZE LOGIC ---
  const handleResizePointerDown = (e: React.PointerEvent) => {
    if (isViewOnly) return; // View-only: no resizing
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    onSelect(id);

    dragStartPos.current = { x: e.clientX, y: e.clientY };
    // Default size if undefined is mostly for safety, though we try to ensure it's set
    const currentRect = nodeRef.current?.getBoundingClientRect();
    const width = size?.width || (currentRect?.width || 300) / getParentScale();
    const height = size?.height || (currentRect?.height || 200) / getParentScale();
    
    initialSize.current = { width, height };
    
    // Use the resize handle ref for pointer capture
    if (resizeHandleRef.current) {
      resizeHandleRef.current.setPointerCapture(e.pointerId);
    }
  };

  const handleResizePointerMove = (e: React.PointerEvent) => {
    if (!isResizing || !dragStartPos.current || !initialSize.current) return;
    e.preventDefault();
    e.stopPropagation();

    const scale = getParentScale();
    const dx = (e.clientX - dragStartPos.current.x) / scale;
    const dy = (e.clientY - dragStartPos.current.y) / scale;

    const newWidth = Math.max(200, initialSize.current.width + dx);
    const newHeight = Math.max(minHeight, initialSize.current.height + dy);

    if (onResize) {
      onResize(id, { width: newWidth, height: newHeight });
    }
  };

  const handleResizePointerUp = (e: React.PointerEvent) => {
    if (isResizing) {
      setIsResizing(false);
      if (resizeHandleRef.current) {
        resizeHandleRef.current.releasePointerCapture(e.pointerId);
      }
    }
  };

  // Helper to get canvas scale
  const getParentScale = (): number => {
    const container = nodeRef.current?.parentElement;
    if (container) {
        const style = window.getComputedStyle(container);
        const matrix = new DOMMatrix(style.transform);
        return matrix.a; // Scale X
    }
    return 1;
  };

  return (
    <div
      ref={nodeRef}
      // No transition on the shadow: animating a blurred shadow repaints the whole node on every frame
      className={`canvas-node absolute flex flex-col rounded-2xl border bg-white cursor-default ${isSelected ? 'z-50' : 'z-10'}`}
      data-node-id={id}
      data-tone={tone}
      data-selected={isSelected ? 'true' : undefined}
      data-commenting={commentsOpen ? 'true' : undefined}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        width: size ? `${size.width}px` : 'auto',
        height: size ? `${size.height}px` : 'auto',
        minWidth: '200px',
        minHeight: `${minHeight}px`,
        willChange: isDragging ? 'transform, translate' : isResizing ? 'transform, width, height' : 'auto',
      }}
      onPointerDown={(e) => {
          e.stopPropagation(); 
          // Shift-click in a text field extends the text selection there, so it does not select nodes
          const inField = !!(e.target as HTMLElement).closest('input, textarea, select, [contenteditable="true"]');
          onSelect(id, !inField && isAdditive(e));
      }}
    >
      {/* Input Connector (Left) - Hidden for storage browser nodes and view-only */}
      {!hideConnectors && !isViewOnly && (
        <div
          className="no-drag canvas-node-port absolute top-1/2 -left-3 -translate-y-1/2 hover:scale-110 transition-transform cursor-crosshair z-50"
          title="Input"
          onPointerUp={(e) => {
               e.stopPropagation();
               if (onConnectEnd) onConnectEnd(id, e);
          }}
        />
      )}

      {/* Output Connector (Right) - Hidden for storage browser nodes and view-only */}
      {!hideConnectors && !isViewOnly && (
        <div
          className="no-drag canvas-node-port absolute top-1/2 -right-3 -translate-y-1/2 hover:scale-110 transition-transform cursor-crosshair z-50"
          title="Output"
          onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onConnectStart) onConnectStart(id, e);
          }}
        />
      )}

      {/* Drag Handle Header */}
      <div
        ref={dragHandleRef}
        className={`drag-handle canvas-node-head ${
          isViewOnly ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'
        } group touch-none`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={(e) => { if (mayRename && !(e.target as HTMLElement).closest('.no-drag')) { e.stopPropagation(); setRenaming(true); } }}
      >
        <div className="flex items-center gap-2 min-w-0 select-none">
          {/* The kind's icon in the kind's colour: what a node is, before its title is read */}
          <span className="canvas-node-icon">{titleIcon ?? <GripHorizontal />}</span>
          {renaming ? (
            <TitleField value={title || ''} placeholder={defaultTitle || 'Name'} onDone={finishRename} />
          ) : (
            <span className="canvas-node-title" title={mayRename ? 'Double-click to rename' : undefined} data-node-title>{title || 'Node'}</span>
          )}
          {isViewOnly && <span className="ns-badge ns-badge-warn">VIEW ONLY</span>}
        </div>
        
        {/* The node's buttons together at the right of its header */}
        <div className="flex items-center gap-0.5 shrink-0">
        {/* Rename and delete: hidden for view-only */}
        {mayRename && !renaming && (
          <button
             className="no-drag ns-icon-btn ns-icon-btn-xs node-reveal"
             onClick={(e) => { e.stopPropagation(); setRenaming(true); }}
             onPointerDown={(e) => e.stopPropagation()}
             title="Rename"
             aria-label="Rename node"
          >
              <PenLine />
          </button>
        )}
        {!isViewOnly && (
          <button
             className="no-drag ns-icon-btn ns-icon-btn-xs ns-icon-btn-danger"
             onClick={(e) => {
                 e.stopPropagation();
                 if (onDelete) onDelete(id);
             }}
             title="Delete Node"
             aria-label="Delete node"
          >
              <X />
          </button>
        )}
        </div>
      </div>

      {/* Content: it scrolls, and is skipped while off screen (canvas-node-body, index.css) */}
      <div className="canvas-node-body relative flex-1 flex flex-col rounded-b-2xl">
        {children}
      </div>

      {/* Zoomed out far, the body is skipped and this light stand-in is shown over its place (index.css) */}
      {farView && <div className="canvas-node-far" aria-hidden="true" data-node-far>{farView}</div>}

      {/* Under the card at its left, floating: its comments and reactions, white round buttons
          like the rest of the UI's, then the reactions given. The strip starts at the card's edge,
          so the pointer moving down to it never leaves the node (the buttons show while it is pointed at) */}
      {(onOpenComments || mayReact || shownReactions.length > 0) && (
        <div className="no-drag absolute left-2 top-full pt-2 flex flex-wrap items-center gap-1.5" onPointerDown={(e) => e.stopPropagation()} data-node-reactions>
          {onOpenComments && (
            <button
               type="button"
               className={`relative ns-icon-btn ns-icon-btn-line shadow-hair ${commentsOpen ? 'bg-lavender-soft text-lavender-deep border-[color:var(--lavender)]' : ''} ${hasSocial ? '' : 'node-reveal'}`}
               onClick={(e) => { e.stopPropagation(); onOpenComments(id); }}
               aria-expanded={commentsOpen}
               title={commentCount > 0 ? `${commentCount} comment${commentCount === 1 ? '' : 's'}: open the conversation about this node` : 'Comment on this node'}
               aria-label={commentCount > 0 ? `Comments on this node, ${commentCount}` : 'Comment on this node'}
               data-node-comments={commentCount}
            >
                <MessageSquare />
                {commentCount > 0 && <span className="ns-count absolute -top-1.5 -right-1.5" aria-hidden="true">{commentCount}</span>}
            </button>
          )}
          {mayReact && (
            // The button and its emojis in one place, so a click on the button does not count as one outside them
            <span ref={pickerRef} className="relative">
              <button
                 type="button"
                 className={`ns-icon-btn ns-icon-btn-line shadow-hair ${hasSocial ? '' : 'node-reveal'}`}
                 onClick={(e) => { e.stopPropagation(); setPicking(open => !open); }}
                 aria-expanded={picking}
                 title="React to this node"
                 aria-label="React to this node"
              >
                  <SmilePlus />
              </button>
              {picking && (
                <span className="absolute left-0 top-full mt-1.5 z-50 ns-menu flex items-center gap-0.5 px-1" role="menu" data-node-reaction-picker>
                  {REACTIONS.map(emoji => {
                    const mine = shownReactions.find(r => r.emoji === emoji)?.mine;
                    return (
                      <button key={emoji} type="button" role="menuitemcheckbox" aria-checked={!!mine} onClick={(e) => { e.stopPropagation(); react(emoji); }} className={`w-8 h-8 shrink-0 grid place-items-center rounded-full text-lg hover:bg-lavender-soft ${mine ? 'bg-lavender-soft' : ''}`} aria-label={`React with ${emoji}`}>
                        {emoji}
                      </button>
                    );
                  })}
                </span>
              )}
            </span>
          )}
          {shownReactions.map(({ emoji, count, mine, who }) => (
            <button
              key={emoji}
              type="button"
              disabled={!mayReact}
              aria-pressed={mine}
              onClick={(e) => { e.stopPropagation(); react(emoji); }}
              className={`ns-chip gap-1 ${mine ? 'ns-chip-lavender' : ''}`}
              title={reactorsLabel(who, myId, reactorNames)}
              data-reaction={emoji}
            >
              <span className="text-sm leading-none">{emoji}</span> {count}
            </button>
          ))}
        </div>
      )}

      {/* Resizer Handle - hidden for view-only */}
      {onResize && !isViewOnly && (
        <div
            ref={resizeHandleRef}
            className="no-drag absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize flex items-end justify-end p-1 z-50 touch-none"
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
        >
             {/* Visual corner marker */}
             <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-ink-faint">
                <path d="M8 8H0L8 0V8Z" fill="currentColor" fillOpacity="0.5"/>
             </svg>
        </div>
      )}
    </div>
  );
};

// Memoize to prevent re-renders when props haven't changed
/** The node's name being typed in its header: Enter or a click elsewhere keeps it, Esc leaves it as it was */
const TitleField: React.FC<{ value: string; placeholder: string; onDone: (name: string | null) => void }> = ({ value, placeholder, onDone }) => {
  const [text, setText] = useState(value);
  const done = useRef(false);
  const finish = (name: string | null) => { if (done.current) return; done.current = true; onDone(name); };
  return (
    <input
      type="text"
      value={text}
      autoFocus
      maxLength={80}
      placeholder={placeholder}
      aria-label="Node name"
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => setText(e.target.value)}
      onPointerDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') { e.preventDefault(); finish(text); }
        else if (e.key === 'Escape') { e.preventDefault(); finish(null); }
      }}
      onBlur={() => finish(text)}
      className="no-drag canvas-node-title min-w-0 w-full max-w-[220px] rounded-md bg-white/80 px-1.5 -mx-1.5 outline-none ring-1 ring-[color:var(--tone-line)] focus:ring-2 focus:ring-[color:var(--tone-deep)] select-text"
      data-node-title-field
    />
  );
};

export const BaseNode = React.memo(BaseNodeComponent);